import { useEffect, useState } from "react";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";
import AppLayout from "../components/AppLayout";
import { ErrorNote } from "../components/Feedback";
import { Btn, Card, Field, Input, Textarea } from "../components/Shared";
import { SuccessNote } from "./admin/parts";

// The signed-in account's own profile (N5): first and last name (required), institute and
// address (optional, free text), and the e-mail, read-only — it is the sign-in identifier,
// Keycloak's for a Keycloak account and an administrator's to change for a local one.
//
//   PATCH /api/me {first_name, last_name, institute, address}   only the fields that changed;
//                 "" clears an optional one (sent as null) -> MeOut
//   POST /api/me/profile/submit   {confirming_curator_id?} incomplete -> submitted; 422 names what is
//                 missing -> MeOut
//   GET /api/me/profile/curators  [{id, name, institute}] (GB, 2026-10-21): who a new account may name
//                 to confirm it; no e-mails. "I don't know — the administrators decide" sends none.
//
// ProfileForm is used by the Account page (a confirmed account edits its profile) and by
// ProfileGatePage, which ProtectedRoute shows IN PLACE OF every page while the account's
// profile_state is not "confirmed" (a Keycloak self-registration): the server answers 403 to
// everything but GET /auth/me, PATCH /me, POST /me/profile/submit and POST /auth/logout until an
// administrator confirms the account (Management -> Users, "Waiting for confirmation").

const FIELDS = ["first_name", "last_name", "institute", "address"];

function initial(user) {
  return Object.fromEntries(FIELDS.map(k => [k, user?.[k] || ""]));
}

function changes(form, user) {
  const out = {};
  for (const k of FIELDS) {
    const v = form[k].trim();
    if (v !== (user[k] || "")) out[k] = v || null;
  }
  return out;
}

const NOBODY = "";

/** The curator who confirms the account (GB): loaded only where the profile can be submitted. */
function useCurators(enabled) {
  const [curators, setCurators] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    api.get("/me/profile/curators")
      .then(list => { if (live) setCurators(list); })
      .catch(e => { if (live) setError(e); });
    return () => { live = false; };
  }, [enabled]);
  return { curators, error };
}

function CuratorChoice({ curators, error, value, onChange }) {
  return (
    <Field label="Who confirms your account?"
           hint="The curator you work with is told that you are waiting and can confirm your account. If you do not know, the administrators decide.">
      <select value={value} onChange={e => onChange(e.target.value)} disabled={!curators}
              style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14, fontFamily: "inherit", background: "#fff" }}>
        <option value={NOBODY}>I don't know — the administrators decide</option>
        {(curators || []).map(c => (
          <option key={c.id} value={String(c.id)}>{c.institute ? `${c.name} (${c.institute})` : c.name}</option>
        ))}
      </select>
      {error && <div style={{ fontSize: 12, color: "#b91c1c", marginTop: 4 }}>The list of curators could not be loaded; the administrators will decide.</div>}
    </Field>
  );
}

export function ProfileForm({ allowSubmit = false }) {
  const { user, updateUser } = useAuth();
  const [form, setForm] = useState(() => initial(user));
  const [curator, setCurator] = useState(NOBODY);
  const { curators, error: curatorsError } = useCurators(allowSubmit);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  // Saves what changed; returns the new MeOut (or the current user when nothing changed).
  const saveChanges = async () => {
    const payload = changes(form, user);
    if (Object.keys(payload).length === 0) return user;
    const me = await api.patch("/me", payload);
    updateUser(me);
    setForm(initial(me));
    return me;
  };

  const save = async e => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      await saveChanges();
      setNotice("Your profile is saved.");
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const submit = async () => {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      await saveChanges();
      const me = await api.post("/me/profile/submit",
                                { confirming_curator_id: curator === NOBODY ? null : Number(curator) });
      updateUser(me);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  const emailHint = user.auth_source === "keycloak"
    ? "Your sign-in identifier. It comes from Keycloak; change it there."
    : "Your sign-in identifier. An administrator changes it.";

  return (
    <form onSubmit={save} aria-label="Your profile" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <ErrorNote error={error} />
      <SuccessNote>{notice}</SuccessNote>
      <div style={{ display: "grid", gap: 14, gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))" }}>
        <Field label="First name" required>
          <Input value={form.first_name} onChange={set("first_name")} autoComplete="given-name" />
        </Field>
        <Field label="Last name" required>
          <Input value={form.last_name} onChange={set("last_name")} autoComplete="family-name" />
        </Field>
        <Field label="E-mail" hint={emailHint}>
          <Input value={user.email || ""} readOnly />
        </Field>
        <Field label="Institute" hint="Optional.">
          <Input value={form.institute} onChange={set("institute")} autoComplete="organization" />
        </Field>
      </div>
      <Field label="Address" hint="Optional.">
        <Textarea value={form.address} onChange={set("address")} autoComplete="street-address" />
      </Field>
      {allowSubmit && (
        <CuratorChoice curators={curators} error={curatorsError} value={curator} onChange={setCurator} />
      )}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <Btn type="submit" variant={allowSubmit ? "secondary" : "primary"} disabled={busy}>
          {busy ? "Saving…" : "Save profile"}
        </Btn>
        {allowSubmit && (
          <Btn onClick={submit} disabled={busy}>Submit for confirmation</Btn>
        )}
      </div>
    </form>
  );
}

export function ProfileGatePage() {
  const { user, retryAuth } = useAuth();
  const submitted = user.profile_state === "submitted";
  return (
    <AppLayout title="Your profile">
      <Card style={{ marginBottom: 20 }}>
        {submitted ? (
          <div role="status" style={{ fontSize: 14, color: "#1a1a1a", marginBottom: 18 }}>
            <p style={{ margin: "0 0 8px", fontWeight: 600 }}>
              {user.confirming_curator_name
                ? `Your profile is waiting for ${user.confirming_curator_name} or an administrator to confirm it.`
                : "Your profile is waiting for an administrator to confirm it."}
            </p>
            <p style={{ margin: 0, color: "#4b5563" }}>
              Until then your account reaches only this page. You can still correct your profile
              below. Once it is confirmed, the portal opens here.
            </p>
          </div>
        ) : (
          <div style={{ fontSize: 14, marginBottom: 18 }}>
            <p style={{ margin: "0 0 8px", fontWeight: 600 }}>Welcome to Unio. Complete your profile first.</p>
            <p style={{ margin: 0, color: "#4b5563" }}>
              Fill in at least your first and last name, choose the curator who confirms your
              account if you know one, and submit the profile. The curator or an administrator then
              confirms it; until then your account reaches only this page.
            </p>
          </div>
        )}
        <ProfileForm key={user.profile_state} allowSubmit={!submitted} />
        {submitted && (
          <div style={{ marginTop: 16 }}>
            <Btn variant="secondary" onClick={retryAuth}>Check again</Btn>
          </div>
        )}
      </Card>
    </AppLayout>
  );
}
